import { css } from 'aphrodite';
import { Button } from '../../../../../web/components-v2/Button';
import { SettingDependencyChunk } from './SettingDependencyChunk';
import { getEntryKey, ISettingEntry } from './settingEntry';
import { mapDependentsToParents, toResultChunks } from './settingsDependency';
import { ISettingsGroup } from './settingsGroups';
import { settingsHubStyles as styles } from './settingsHubStyles';
import { filterSettings } from './settingsSearch';

interface IProps {
	entries: ISettingEntry[];
	groups: ISettingsGroup[];
	query: string;
	onClear: () => void;
}

/**
 * Matches across every group at once, rather than seven columns each filtered in place. Groups
 * with nothing in them collapse to a chip, so the page no longer shows a row of bare headings.
 *
 * A matched dependent is shown beneath the setting that governs it, even when that parent did not
 * match, so a locked result is never presented without the control that unlocks it.
 */
export function SettingsSearchResults({ entries, groups, query, onClear }: IProps) {
	const visibleEntries = entries.filter(entry => groups.some(group => group.category === entry.category));
	// Resolved before filtering, since a match's parent is often not itself a match.
	const parents = mapDependentsToParents(visibleEntries);
	const matched = filterSettings(visibleEntries, { query });

	const groupsWithMatches = groups
		.map(group => ({ group, matches: matched.filter(entry => entry.category === group.category) }))
		.filter(({ matches }) => matches.length > 0);

	const groupsWithoutMatches = groups.filter(
		group => !groupsWithMatches.some(({ group: matchedGroup }) => matchedGroup.category === group.category)
	);

	return (
		<>
			<div className={css(styles.searchSummary)}>
				<span>
					{matched.length} of {visibleEntries.length} settings match
				</span>
				<Button variant='link' size='sm' onClick={onClear} styles={[styles.inlineLinkButton]}>
					Clear
				</Button>
			</div>

			{matched.length === 0 ? (
				<div className={css(styles.groupPanel)}>
					<div className={css(styles.emptyState)}>No settings match &ldquo;{query}&rdquo;.</div>
				</div>
			) : (
				<div className={css(styles.groupPanel)}>
					{groupsWithMatches.map(({ group, matches }, groupIndex) => (
						<div key={group.category}>
							<div className={css(styles.resultGroupHeader, groupIndex === 0 && styles.resultGroupHeaderFirst)}>
								{group.category} · {matches.length} {matches.length === 1 ? 'match' : 'matches'}
							</div>
							<div className={css(styles.groupPanelBody)}>
								{toResultChunks(matches, parents).map(({ parent, parentMatched, dependents }, index) => (
									<SettingDependencyChunk
										key={getEntryKey(parent, index)}
										parent={parent}
										dependents={dependents}
										// A parent shown only for context did not match, so label it and keep the count honest.
										parentBadge={
											parentMatched ? undefined : <span className={css(styles.contextBadge)}>parent setting</span>
										}
									/>
								))}
							</div>
						</div>
					))}
				</div>
			)}

			{matched.length > 0 && groupsWithoutMatches.length > 0 && (
				<div className={css(styles.noMatchRow)}>
					<span>No matches in:</span>
					{groupsWithoutMatches.map(group => (
						<span key={group.category} className={css(styles.noMatchChip)}>
							{group.category}
						</span>
					))}
				</div>
			)}
		</>
	);
}
